import { Api } from "@/services/ApiService"
import { i18n } from '@/i18n'

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  data?: {
    user_form_submission_id?: string;
    [key: string]: any;
  };
}

export interface NotificationResponse {
  data: Notification[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
}

export type NotificationFilter = 'all' | 'unread';

class NotificationService {
  async getAllNotifications(filter: NotificationFilter = 'all') {
    try {
      const params = filter !== 'all' ? { read_status: filter } : {};
      const responseResult = await Api.get('/notifications', { params });
      const response = responseResult.ok ? responseResult.data : null

      if (!response || response?.error) {
        return {
          success: false,
          message: response?.message || i18n.global.t("Failed to fetch notifications."),
        }
      }

      return {
        success: true,
        message: i18n.global.t("Successfully fetched notifications"),
        data: response,
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return {
        success: false,
        message: i18n.global.t("An unexpected error occurred while fetching notifications"),
      }
    }
  }

  async markAsRead(notificationId: string) {
    try {
      const response = await Api.patch(`/notifications/${notificationId}/read`);

      if (!response || response?.error) {
        return {
          success: false,
          message: response?.message || i18n.global.t("Failed to mark notification as read."),
        }
      }

      return {
        success: true,
        message: i18n.global.t("Successfully marked notification as read"),
        data: response,
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return {
        success: false,
        message: i18n.global.t("An unexpected error occurred while marking notification as read"),
      }
    }
  }

  async markAllAsRead(notifications: Notification[]) {
    try {
      const response = await Api.put('/notifications/read', {
        ids: notifications.map((notification: Notification) => notification.id),
      });

      if (!response || response?.error) {
        return {
          success: false,
          message: i18n.global.t('Failed to mark notifications as read.'),
        }
      }

      return {
        success: true,
        message: i18n.global.t('Marked {count} notifications as read', { count: response.total }, response.total),
        data: response,
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return {
        success: false,
        message: i18n.global.t("An unexpected error occurred while marking all notifications as read"),
      }
    }
  }
}

export default new NotificationService() 